import { useContext, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { AppContext } from "../context/app.context";
import { ModelCxContext } from "../context/modelCx.context";
import {
  MONGODB_AWSGW_QUERIES,
  MONGODB_AWSGW_APIKEY,
  MONGODB_DATASOURCE_TS,
  MONGODB_COLLECTION_LABORCATALOG,
} from "../utility/keys";
import {
  buildLaborAttributeBySku,
  buildLaborAttributeOptions,
} from "../utility/laborAttributesGate";
import { resolveBackendMode } from "../utility/backendConfig";
import { refs } from "../convex/apiRefs";
import { convexReadEnvelope } from "../convex/convexRead";

// - - - - - - - - - -
// #194 (2026-06-09) — fetch the LaborCatalog collection (itemNo → attribute)
// once and expose it as a Map<itemNo→attribute> for the capacity recommender's
// Labor-Catalog Attribute gate.
//
// The LaborCatalog is small (467 rows) and near-static (manually maintained,
// like Stores — see docs/manual-mongo-collections.md), so we fetch it ONCE per
// session with a long staleTime and never poll. The Lambda returns only rows
// with a non-empty `attribute` (the blank ones never gate), keeping the payload
// tiny; `buildLaborAttributeBySku` builds the trimmed-string lookup.
//
// SAFE DEFAULT: on any failure / before load, `laborAttributeBySku` is an empty
// Map, which makes the gate INERT (recommends everything) — pre-#194 behavior.
//
// Returns:
//   { laborAttributeBySku: Map<string,string>, isLoading, isError, error }

async function fetchLaborCatalogLambda(body) {
  const response = await fetch(MONGODB_AWSGW_QUERIES, {
    method: "POST",
    mode: "cors",
    credentials: "omit",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "x-api-key": MONGODB_AWSGW_APIKEY,
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new Error(
      `useLaborCatalog HTTP ${response.status}: ${response.statusText}`
    );
  }
  const result = await response.json();
  if (result.errorType || result.errorMessage) {
    throw new Error(result.errorMessage || result.errorType || "Unknown error");
  }
  return result;
}

export function useLaborCatalog({ enabled = true } = {}) {
  const { appEnvirons } = useContext(AppContext);
  const { tsUserDocument } = useContext(ModelCxContext);
  const isConvexOnly = resolveBackendMode(tsUserDocument) === "convexOnly";
  const mongoDatabase = appEnvirons?.mongoDb;

  // convexOnly reads LaborCatalog from Convex (never the Lambda); convexOnly
  // doesn't depend on mongoDatabase being resolved.
  const queryEnabled = !!enabled && (isConvexOnly || !!mongoDatabase);

  const body = useMemo(
    () => ({
      method: "getLaborCatalog",
      dataSource: MONGODB_DATASOURCE_TS,
      database: mongoDatabase,
      collection: MONGODB_COLLECTION_LABORCATALOG,
    }),
    [mongoDatabase]
  );

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["getLaborCatalog", isConvexOnly ? "cx" : "mongo"],
    queryFn: () =>
      isConvexOnly
        ? convexReadEnvelope(refs.getLaborCatalog, {}, "list")
        : fetchLaborCatalogLambda(body),
    enabled: queryEnabled,
    staleTime: 60 * 60 * 1000, // 1 hour — near-static, manually maintained
    refetchOnWindowFocus: false,
  });

  const rawDocs = useMemo(() => {
    const raw = data?.message?.result;
    return Array.isArray(raw) ? raw : [];
  }, [data]);

  const laborAttributeBySku = useMemo(
    () => buildLaborAttributeBySku(rawDocs),
    [rawDocs]
  );

  // #194 follow-up — the distinct, sorted set of attribute values in the
  // catalog (the current "categories"). Drives CrewDetailModal's Attributes
  // tab so its checkbox list is live from the catalog (single source of truth)
  // instead of a hardcoded list that drifts as Brandon renames things.
  const laborAttributeOptions = useMemo(
    () => buildLaborAttributeOptions(rawDocs),
    [rawDocs]
  );

  return {
    laborAttributeBySku,
    laborAttributeOptions,
    isLoading: !!isLoading,
    isError: !!isError,
    error,
  };
}
